/**
 * Groups an array of items by a key determined by the callback function.
 * Returns a Map where keys are the group keys and values are arrays of items.
 *
 * @example
 * const users = [
 *   { id: 1, role: 'admin', name: 'Alice' },
 *   { id: 2, role: 'user', name: 'Bob' },
 *   { id: 3, role: 'admin', name: 'Charlie' }
 * ]
 * const grouped = groupBy(users, user => user.role)
 * // Returns Map { 'admin' => [...], 'user' => [...] }
 */
export function groupBy<T, K>(items: T[], keyFn: (item: T) => K): Map<K, T[]> {
  const map = new Map<K, T[]>()

  for (const item of items) {
    const key = keyFn(item)
    const group = map.get(key) || []
    group.push(item)
    map.set(key, group)
  }

  return map
}

/**
 * Creates a Map from an array of items, keyed by the result of the callback function.
 * If multiple items produce the same key, the last one wins.
 *
 * @example
 * const users = [
 *   { id: 1, name: 'Alice' },
 *   { id: 2, name: 'Bob' }
 * ]
 * const userMap = keyBy(users, user => user.id)
 * // Returns Map { 1 => { id: 1, name: 'Alice' }, 2 => { id: 2, name: 'Bob' } }
 */
export function keyBy<T, K>(items: T[], keyFn: (item: T) => K): Map<K, T> {
  const map = new Map<K, T>()

  for (const item of items) {
    const key = keyFn(item)
    map.set(key, item)
  }

  return map
}

/**
 * Calculates the sum of all numbers in an array.
 *
 * @example
 * const numbers = [1, 2, 3, 4, 5]
 * const total = sum(numbers) // Returns 15
 */
export function sum(items: number[]): number {
  return items.reduce((total, item) => total + item, 0)
}

/**
 * Calculates the sum by applying a callback function to each item and summing the results.
 *
 * @example
 * const opportunities = [
 *   { id: 1, estimatedValue: 100 },
 *   { id: 2, estimatedValue: 200 }
 * ]
 * const totalValue = sumBy(opportunities, opp => opp.estimatedValue) // Returns 300
 */
export function sumBy<T>(
  items: readonly T[],
  valueFn: (item: T) => number,
): number {
  return items.reduce((total, item) => total + (valueFn(item) || 0), 0)
}

/**
 * Partitions an array into two arrays based on a predicate function.
 * Returns a tuple where the first array contains items that match the predicate,
 * and the second array contains items that don't match.
 *
 * @example
 * const sections = [
 *   { key: 'header', layout: 'main' },
 *   { key: 'sidebar', layout: 'sidebar' },
 *   { key: 'content', layout: 'main' }
 * ]
 * const [mainSections, sidebarSections] = partition(
 *   sections,
 *   section => !section.layout || section.layout === 'main'
 * )
 * // mainSections: [{ key: 'header', layout: 'main' }, { key: 'content', layout: 'main' }]
 * // sidebarSections: [{ key: 'sidebar', layout: 'sidebar' }]
 */
export function partition<T>(
  items: T[],
  predicate: (item: T) => boolean,
): [T[], T[]] {
  const truthy: T[] = []
  const falsy: T[] = []

  for (const item of items) {
    if (predicate(item)) {
      truthy.push(item)
    } else {
      falsy.push(item)
    }
  }

  return [truthy, falsy]
}

type SortCriterion<T> =
  | ((item: T) => unknown)
  | [(item: T) => unknown, 'asc' | 'desc']

/**
 * Sorts an array by one or more criteria without mutating the original array.
 * Each criterion can be a callback function or a tuple of [callback, order].
 * Default order is 'asc' (ascending).
 *
 * @example
 * // Simple usage (ascending by default)
 * const sorted = sortBy(users, u => u.name)
 *
 * @example
 * // With explicit order
 * const sorted = sortBy(users, [u => u.createdAt, 'desc'])
 *
 * @example
 * // Multiple criteria
 * const sorted = sortBy(
 *   users,
 *   [u => u.role, 'asc'],
 *   [u => u.createdAt, 'desc']
 * )
 *
 * @example
 * // Mixed syntax
 * const sorted = sortBy(
 *   users,
 *   u => u.department,  // defaults to 'asc'
 *   [u => u.salary, 'desc']
 * )
 */
export function sortBy<T>(array: T[], ...criteria: SortCriterion<T>[]): T[] {
  // Create a copy to avoid mutation
  const result = [...array]

  // Early return for empty array or no criteria
  if (result.length === 0 || criteria.length === 0) {
    return result
  }

  // Parse criteria into normalized format
  const normalizedCriteria: Array<{
    fn: (item: T) => unknown
    order: 'asc' | 'desc'
  }> = criteria.map((criterion) => {
    if (typeof criterion === 'function') {
      return { fn: criterion, order: 'asc' }
    }
    return { fn: criterion[0], order: criterion[1] || 'asc' }
  })

  // Sort using all criteria
  result.sort((a, b) => {
    for (const { fn, order } of normalizedCriteria) {
      const aValue = fn(a)
      const bValue = fn(b)

      // Handle null/undefined values
      if (aValue == null && bValue == null) continue
      if (aValue == null) return order === 'asc' ? 1 : -1
      if (bValue == null) return order === 'asc' ? -1 : 1

      // Compare values
      let comparison = 0

      if (aValue < bValue) {
        comparison = -1
      } else if (aValue > bValue) {
        comparison = 1
      }

      // If values are different, return the comparison
      if (comparison !== 0) {
        return order === 'desc' ? -comparison : comparison
      }
      // If values are equal, continue to next criterion
    }

    return 0
  })

  return result
}

/**
 * Casts value to an array if it's not one.
 * If value is already an array, returns it unchanged.
 * If value is null or undefined, returns an empty array.
 * Otherwise, wraps the value in an array.
 *
 * @example
 * castArray(1) // Returns [1]
 * castArray([1, 2]) // Returns [1, 2]
 * castArray(null) // Returns []
 * castArray(undefined) // Returns []
 * castArray('hello') // Returns ['hello']
 */
export function castArray<T>(value: T | T[] | null | undefined): T[] {
  if (value == null) return []
  return Array.isArray(value) ? value : [value]
}

type Falsy = false | 0 | '' | null | undefined

/**
 * Type guard that filters out falsy values (null, undefined, false, 0, '', NaN).
 * Use with array.filter() to remove falsy values while maintaining proper TypeScript types.
 *
 * @example
 * const values = [1, null, 2, undefined, 3, false, 0]
 * const truthy = values.filter(isTruthy) // Type: number[], Value: [1, 2, 3]
 *
 * @example
 * const booleans = [true, false, null]
 * const filtered = booleans.filter(isTruthy) // Type: true[], correctly excludes false
 *
 * @example
 * const conditions = [
 *   eq(table.id, id),
 *   includeDeleted ? undefined : isNull(table.deletedAt),
 *   rowLevelFilter
 * ].filter(isTruthy) // Type: SQL[]
 */
export function isTruthy<T>(value: T): value is Exclude<T, Falsy> {
  return Boolean(value)
}

/**
 * Creates an array with unique values based on the result of the callback function.
 * The first occurrence of each unique value is kept.
 *
 * @example
 * const users = [
 *   { id: 1, name: 'Alice' },
 *   { id: 2, name: 'Bob' },
 *   { id: 1, name: 'Alice (duplicate)' }
 * ]
 * const unique = uniqBy(users, user => user.id)
 * // Returns [{ id: 1, name: 'Alice' }, { id: 2, name: 'Bob' }]
 *
 * @example
 * const rooms = [
 *   { id: 1, type: 'customer_service' },
 *   { id: 2, type: 'general' },
 *   { id: 1, type: 'customer_service' }
 * ]
 * const uniqueRooms = uniqBy(rooms, room => room.id)
 * // Returns [{ id: 1, type: 'customer_service' }, { id: 2, type: 'general' }]
 */
export function uniqBy<T, K>(items: T[], keyFn: (item: T) => K): T[] {
  const seen = new Set<K>()
  const result: T[] = []

  for (const item of items) {
    const key = keyFn(item)
    if (!seen.has(key)) {
      seen.add(key)
      result.push(item)
    }
  }

  return result
}

/**
 * Returns a shallow copy of an object with the given keys removed.
 * The return type intentionally preserves T so callers passing a wider
 * schema-derived type (where the omitted keys are optional) still satisfy
 * the original type contract.
 *
 * @example
 * omitFields({ a: 1, b: 2, c: 3 }, ['b', 'c']) // { a: 1 }
 */
export function omitFields<T extends object>(
  obj: T,
  keys: ReadonlyArray<keyof T>,
): T {
  const result = { ...obj }
  for (const key of keys) {
    delete result[key]
  }
  return result
}

// Finnish collation: `sortBy` compares with </>, which puts Ä and Ö after Z.
const optionCollator = new Intl.Collator('fi')

/**
 * Sorts dropdown options alphabetically by label, without mutating the input.
 * Use for options derived from query data — list endpoints return rows in id
 * order, so an unsorted dropdown lists people by when their account was made.
 * Hand-written enum lists keep their own order and must not go through this.
 *
 * @example
 * const options = sortOptions(users.map(u => ({ value: String(u.id), label: fullName(u) })))
 */
export function sortOptions<T extends { label: string }>(options: T[]): T[] {
  return [...options].sort((a, b) => optionCollator.compare(a.label, b.label))
}
